<script lang="ts" setup>
	import { useClipboard, useShare } from '@vueuse/core'
	defineProps<{ enablButtonLabel?: boolean; provider?: string }>()
	const { share, isSupported } = useShare()
	const showCareCordinator = shallowRef(true)
	const { getSupportContact } = useStore()
	const { copy, isSupported: supportsCopy } = useClipboard()
	const manager = computed(() => getSupportContact('manager'))
	const copyItem = async () => {
		if (!supportsCopy.value) {
			useNotification().alert({
				status: 400,
				message: 'Copy to clipboard is not supported on this device'
			})
			return
		}
		resolve(async () => {
			await copy(manager.value?.line || '')

			useNotification().alert({
				status: 200,
				message: 'Copied to clipboard'
			})
		})
	}
	const shareNumber = () => {
		if (!isSupported.value) {
			useNotification().alert({
				status: 400,
				message: 'Sharing is not supported on this device'
			})
			return
		}
		share({
			title: 'Care Co-ordinator',
			text: `Share this number with your staff as their care co-ordinator. They can use this number in case they are being delayed at the hospital. ${getSupportContact('manager')?.line ?? ''}`
		})
	}
</script>
<template>
	<div>
		<transition
			name="zoom"
			enter-active-class=" animate__animated animate__zoomIn"
			leave-active-class="animate__animated animate__bounceOutUp animate__fast"
		>
			<div
				v-if="showCareCordinator"
				class="my-5 w-full gap-1.5 rounded-lg border border-purple-500 bg-purple-50 px-3 py-4 md:my-[40px]"
			>
				<div class="items-centerw-full hidden flex-wrap justify-between space-y-3 sm:flex xl:space-y-0">
					<div class="flex items-center gap-2">
						<tools-icon dir="communication" icon="headset" :width="24" :height="24" />
						<div :class="enablButtonLabel ? 'text-base' : 'text-sm'">
							<p class="inline">
								<span
									class="font-semibold text-purple-500"
									:class="enablButtonLabel ? 'text-lg md:text-xl' : 'text-sm'"
								>
									{{ getSupportContact('manager')?.linkText ?? 'N/A' }}
								</span>
								-
								<span class="font-medium leading-[140%] text-gray-600">
									Your staff’s first contact for hospital-related issues.
								</span>
							</p>
							<br />
							<p v-if="provider" class="inline leading-[140%] text-gray-600">
								<span class="font-semibold">Note:</span> when they get to the hospital, they should
								indicate they are from <span class="font-bold">{{ provider || '' }} HMO</span>.
							</p>
						</div>
					</div>

					<div class="flex w-full items-center justify-end gap-2 lg:w-auto">
						<ui-button
							class="border border-primary-2 bg-white px-0 py-0 sm:px-0 sm:py-0 sm:text-sm"
							:class="{ 'h-[44px] w-[85px]': enablButtonLabel, 'size-10': !enablButtonLabel }"
							@click="shareNumber"
						>
							<tools-icon
								icon="share"
								dir="documents"
								path-class="stroke-primary-2 fill-primary-2"
								:width="20"
								:height="20"
							/>
							<span v-if="enablButtonLabel" class="font-semibold text-primary-2">Share</span>
						</ui-button>
						<ui-button
							class="bg-primary-2 px-0 py-0 text-white sm:px-0 sm:py-0 sm:text-sm"
							:class="{ 'h-[44px] w-[85px]': enablButtonLabel, 'size-10': !enablButtonLabel }"
							@click="copyItem"
						>
							<tools-icon
								icon="products-active"
								dir="fill"
								path-class="fill-white"
								:width="20"
								:height="20"
							/>
							<span v-if="enablButtonLabel" class="font-semibold text-white">Copy</span>
						</ui-button>
						<ui-button
							class="hidden size-6 flex-none flex-col justify-center rounded-full bg-gray-600 p-0 sm:flex sm:p-0"
							@click="showCareCordinator = false"
						>
							<tools-icon icon="cancel" dir="line" :width="10" :height="10" path-class="stroke-white" />
						</ui-button>
					</div>
				</div>

				<!-- Mobile -->
				<div class="flex w-full items-start gap-2 sm:hidden">
					<div
						class="flex size-7 flex-none items-center justify-center rounded-full border-[0.5px] border-purple-200"
					>
						<tools-icon dir="fill" icon="phone" :width="20" :height="20" />
					</div>

					<div class="description">
						<p class="text-xs font-medium leading-4 text-[#475466]">
							Your staff’s first contact for hospital-related issues.
						</p>
						<div class="flex items-center justify-between pb-2 pt-2">
							<span class="justify-start text-base font-bold leading-[22.40px] text-purple-500">
								{{ getSupportContact('manager')?.linkText ?? 'N/A' }}
							</span>
							<div class="inline-flex items-center gap-1">
								<ui-button
									class="flex size-6 flex-none items-center justify-center rounded-full bg-purple-500 p-0"
									@click="copyItem"
								>
									<tools-icon
										icon="copy"
										dir="fill"
										path-class="fill-white"
										:width="16"
										:height="16"
									/>
								</ui-button>

								<ui-button
									class="flex size-6 flex-none items-center justify-center rounded-full border border-purple-500 bg-white p-0"
									@click="shareNumber"
								>
									<tools-icon
										icon="share"
										dir="documents"
										path-class="fill-purple-500"
										:width="16"
										:height="16"
									/>
								</ui-button>
							</div>
						</div>
						<div class="text-pretty border-t-[2px] border-purple-100 pt-2 text-xs">
							<p v-if="provider" class="leading-[140%] text-gray-600">
								Note: Staff should indicate they are from
								<span class="font-bold">{{ provider || '' }} HMO</span> when they get to the hospital.
							</p>
						</div>
					</div>

					<ui-button
						class="ml-2 flex size-4 flex-none flex-col justify-center rounded-full bg-gray-600 p-0 sm:p-0"
						@click="showCareCordinator = false"
					>
						<tools-icon icon="cancel" dir="line" :width="13" :height="13" path-class="stroke-white" />
					</ui-button>
				</div>
			</div>
		</transition>
	</div>
</template>
